import { memo } from 'react';

import {
  Button as CustomButton,
  DateCell,
  dateTimeFormatter,
  formatNumber,
  formattedCell,
  PageTitle
} from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { BtnGroup, BtnToolbar, Col, Row } from '@dec/shared';
import { type ColDef, type ValueFormatterParams } from 'ag-grid-community';
import GridApplication from 'app/components/grid/gridApplication';
import env from 'env';
import { CurrencyExchange, PeopleFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useCurrentAccount } from '../hooks/useCurrentAccount';

interface Props {}

export const CurrentAccountTransactionPage = memo((props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  // se recupera identificador de cuenta corriente
  const { accountId } = useParams<{
    accountId: string;
  }>();

  const {
    data: account,
    isLoading: isLoadingAccount,
    error: accountError
  } = useCurrentAccount(accountId);

  // se recupera información de la cuenta
  // const account = await currentAccountApi.getAccount(accountId);

  const navigate = useNavigate();
  const colDef: Array<ColDef> = [
    {
      headerName: t('taxpayer-current-account.label.date'),
      field: 'createdDate',
      ...formattedCell(dateTimeFormatter, DateCell)
    },
    {
      headerName: t('common.label.period'),
      field: 'period'
    },
    {
      headerName: t('taxpayer-current-account.label.transactionType'),
      field: 'transactionTypeName'
    },
    {
      headerName: t('taxpayer-current-account.label.debit'),
      field: 'value',
      cellStyle: {
        textAlign: 'right'
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value < 0 ? formatNumber(Math.abs(params.value)) : '';
      }
    },
    {
      headerName: t('taxpayer-current-account.label.credit'),
      field: 'value',
      cellStyle: {
        textAlign: 'right'
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value > 0 ? formatNumber(Math.abs(params.value)) : '';
      }
    },
    {
      headerName: t('taxpayer-current-account.label.document'),
      field: 'documentNumber',
      // https://github.com/ag-grid/ag-grid/issues/5226
      cellRenderer: function (params: any) {
        // se construye el link y el texto
        let url = '';
        const text: string = params.data.documentNumber;

        if (params.data.taxReturnId) {
          url = `${env.publicUrl}/taxpayer/tax-returns/preview/${params.data.taxReturnId}`;
        }
        if (params.data.taxReturnPaymentId) {
          url =
            `${env.publicUrl}/taxpayer/taxreturn-payment/view?taxReturnId=` +
            params.data.taxReturnId +
            '&paymentId=' +
            params.data.taxReturnPaymentId;
        }

        if (params.data.transactionAdjustmentId) {
          url =
            `${env.publicUrl}/taxpayer/transaction-adjustment/view?id=` +
            params.data.transactionAdjustmentId;
        }

        if (!url) {
          // si no tenemos una url por abrir
          return <span> --- </span>;
        }

        // ahora se devuelve el vinculo
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        );
      }
    }
  ];
  return (
    <>
      <PageTitle
        title={t('taxpayer-current-account.title.currentAccountTransaction')}
        subtitle={t('taxpayer-current-account.title.currentAccount')}
        icon={CurrencyExchange}
      />

      <div className="card">
        <div className="card-body px-3">
          {' '}
          <div className="d-flex align-items-start gap-3 mt-0 mb-4">
            <div className="pt-1">
              <PeopleFill size={35} className="text-primary" />
            </div>
            <div>
              <div className="fw-bolder text-primary"> {account?.tin} </div>
              <div className="text-dark">{account?.taxpayerName} </div>
            </div>
          </div>
        </div>
      </div>

      <Row className="mb-5 mt-3">
        <Col>
          <GridApplication
            url={`${env.publicUrl}/current-account/accounts/transactions`}
            columnDefs={colDef}
            search={{ CurrentAccountId: accountId }}
          />
        </Col>
      </Row>

      <div className="mt-5 d-flex">
        <BtnToolbar>
          <BtnGroup>
            <CustomButton
              action={ButtonAction.return}
              variant="secondary"
              onClick={() => navigate('/current-account')}
            />
          </BtnGroup>
        </BtnToolbar>
      </div>
    </>
  );
});

CurrentAccountTransactionPage.displayName = 'CurrentAccountTransactionPage';
